import { Component, OnDestroy, OnInit } from '@angular/core';
import { PrimeNG } from 'primeng/config';
import {
  DL_SIGNUP_URL,
  FAREWELL,
  LOGINURL,
  MAINTENANCE,
  NOTFOUNDURL,
  PASSWORD_RESET_URL,
  SMARTGOALSGENERATOR,
  EMBEDGENERATOR,
  TERMS,
  LEADAUTH,
  RELOAD,
  isEventNetworkingPublicUrl,
  isMemberDatabasePublicUrl,
} from 'src/app/app-routing.module';
import { AuthenticationService } from 'src/app/shared/services/authentication.service';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/state/reducers';
import { UtilsService } from 'src/app/shared/services/utils.service';
import { LEADACTIVATION, PUBLICLOGIN, LEADSIGNUP } from 'src/app/authentication-lead/authentication-lead.module';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';

@Component({
    selector: 'fp-app',
    templateUrl: './app.component.html',
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  private langSubscription: Subscription;
  private readonly publicNoAuthPaths = [`/${SMARTGOALSGENERATOR}`, `/${EMBEDGENERATOR}`];

  constructor(
    private primengConfig: PrimeNG,
    private store: Store<AppState>,
    private utilsService: UtilsService,
    public authService: AuthenticationService,
    private translate: TranslateService,
    private router: Router
  ) {
    const savedLang = localStorage.getItem('lang') || 'en';

    translate.addLangs(['cz', 'en']);
    translate.setFallbackLang('en');
    translate.use(savedLang);
    this.updateBodyLangClass(savedLang);

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        take(1)
      )
      .subscribe(() => void this.bootstrapSessionIfNeeded());
  }
  currentUrl$ = this.store.select(state => state.router?.state?.url?.split('?')[0] || '/');

  ngOnInit() {
    this.primengConfig.ripple.set(true);

    // Subscribe to language changes
    this.langSubscription = this.translate.onLangChange.subscribe(event => {
      this.updateBodyLangClass(event.lang);
      window.dispatchEvent(new Event('languageChanged'));
    });

    this.translate.get('PRIMENG').subscribe(translations => {
      this.primengConfig.setTranslation(translations);
    });

    this.translate.onLangChange.subscribe(() => {
      this.translate.get('PRIMENG').subscribe(translations => {
        this.primengConfig.setTranslation(translations);
      });
    });
  }

  private async bootstrapSessionIfNeeded(): Promise<void> {
    const fromRouter = this.router.url.split('?')[0];
    const fromLocation = typeof window !== 'undefined' ? window.location.pathname : '';
    const isPublicNoAuthRoute =
      this.publicNoAuthPaths.includes(fromRouter) ||
      this.publicNoAuthPaths.includes(fromLocation) ||
      isEventNetworkingPublicUrl(fromRouter) ||
      isEventNetworkingPublicUrl(fromLocation) ||
      isMemberDatabasePublicUrl(fromRouter) ||
      isMemberDatabasePublicUrl(fromLocation);

    if (isPublicNoAuthRoute) {
      return;
    }
    const user = await this.authService.getMe();
    if (user) {
      this.utilsService.loadData2Store(user?.role);
    }
  }

  ngOnDestroy() {
    if (this.langSubscription) {
      this.langSubscription.unsubscribe();
    }
  }

  private updateBodyLangClass(lang: string) {
    // Remove existing language classes
    document.body.classList.remove('lang-cz', 'lang-en');
    // Add current language class
    document.body.classList.add(`lang-${lang}`);
  }

  loginUrls = [LOGINURL, DL_SIGNUP_URL, PASSWORD_RESET_URL, LEADAUTH, LEADSIGNUP, LEADACTIVATION, PUBLICLOGIN];
  noLayoutUrls = [NOTFOUNDURL, MAINTENANCE, TERMS, SMARTGOALSGENERATOR, EMBEDGENERATOR, FAREWELL, RELOAD];

  isLoginLayout(url: string): boolean {
    let urlNoSlash = url.slice(1);
    if (url.includes('activate')) {
      const segments = url.split('/');
      urlNoSlash = segments.slice(1, 3).join('/');
    }
    return this.loginUrls.includes(urlNoSlash);
  }

  isNoLayout(url: string): boolean {
    const urlNoSlash = url.slice(1);
    return this.noLayoutUrls.includes(urlNoSlash) || isEventNetworkingPublicUrl(url) || isMemberDatabasePublicUrl(url);
  }
}
